import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../blocks/DataTable";
import { RegistryWorkspace } from "../blocks/RegistryWorkspace";
import { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../../internal/primitives/button";
import { Label } from "../../internal/primitives/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select";
import { Checkbox } from "../../internal/primitives/checkbox";
import type {
  StatusInventoryEditorField,
  StatusInventoryDetailTab,
  StatusInventoryNotice,
  StatusInventorySectionDetail,
  StatusInventoryTableColumn,
  StatusInventoryTableRow,
  StatusInventoryTone,
  StatusInventoryViewProps,
} from "../../contracts/status-inventory";

// ─── Inline icons ─────────────────────────────────────────────────────────────

function IconSearch({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx={11} cy={11} r={8} /><path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function IconRefresh({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  );
}

function IconPlus({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="M12 5v14" />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ tone }: { tone?: StatusInventoryTone }) {
  return (
    <span
      className={cn(
        "inline-block h-2 w-2 flex-shrink-0 rounded-full",
        tone === "success" ? "bg-success" :
        tone === "warning" ? "bg-warning" :
        tone === "danger"  ? "bg-destructive" :
        tone === "info"    ? "bg-primary/70" :
        "bg-muted-foreground/30"
      )}
    />
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────

function Badge({ label, tone = "neutral" }: { label: string; tone?: StatusInventoryTone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        tone === "success" ? "bg-success/10 text-success" :
        tone === "warning" ? "bg-warning/10 text-warning" :
        tone === "danger"  ? "bg-destructive/10 text-destructive" :
        tone === "info"    ? "bg-primary/10 text-primary" :
        "bg-muted text-muted-foreground"
      )}
    >
      {label}
    </span>
  );
}

// ─── Notice card ──────────────────────────────────────────────────────────────

function NoticeCard({ notice }: { notice: StatusInventoryNotice }) {
  const cls =
    notice.tone === "danger"  ? "bg-destructive/10 border-destructive/30 text-destructive" :
    notice.tone === "warning" ? "bg-warning/10 border-warning/30 text-foreground" :
    notice.tone === "success" ? "bg-muted/30 border-border text-foreground" :
    notice.tone === "info"    ? "bg-primary/10 border-primary/30 text-primary" :
    "bg-card border-border text-foreground";
  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2", cls)}>
      {notice.title && <p className="text-sm font-semibold">{notice.title}</p>}
      <p className="text-sm">{notice.description}</p>
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="divide-y divide-border">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
          <div className="h-2 w-2 rounded-full bg-muted flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-48 rounded bg-muted" />
            <div className="h-2.5 w-28 rounded bg-muted" />
          </div>
          <div className="hidden md:flex gap-8">
            <div className="h-2.5 w-24 rounded bg-muted" />
            <div className="h-2.5 w-20 rounded bg-muted" />
            <div className="h-2.5 w-16 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-border bg-muted/40">
        <span className="text-xl text-muted-foreground/50">∅</span>
      </div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

// ─── Inventory table ──────────────────────────────────────────────────────────

function InventoryTable({
  sectionLabel,
  columns,
  rows,
  selectedRowId,
  onActivateRow,
  onSortChange,
}: {
  sectionLabel: string;
  columns: StatusInventoryTableColumn[];
  rows: StatusInventoryTableRow[];
  selectedRowId?: string | null;
  onActivateRow: (rowId: string) => void;
  onSortChange: StatusInventoryViewProps["onSortChange"];
}) {
  const tableColumns: ColumnDef<StatusInventoryTableRow>[] = [
    {
      id: 'identity', header: sectionLabel, enableSorting: false,
      cell: ({ row }) => <div className="min-w-48 space-y-1">
        <div className="flex items-center gap-2"><StatusDot tone={row.original.badges?.[0]?.tone} /><span className="font-medium">{row.original.title}</span></div>
        {row.original.subtitle && <p className="text-xs text-muted-foreground">{row.original.subtitle}</p>}
        <div className="flex flex-wrap gap-1">{row.original.badges?.map(badge => <Badge key={badge.id} {...badge} />)}</div>
      </div>,
    },
    ...columns.map(column => ({
      id: column.id,
      accessorFn: (row: StatusInventoryTableRow) => row.cells[column.id] || '—',
      header: column.label,
      enableSorting: column.sortable,
    })),
    { id: 'action', header: '', enableSorting: false, cell: ({ row }) => <Button
      variant="ghost" size="sm" disabled={row.original.actionDisabled}
      aria-label={row.original.actionDescription ?? `${row.original.actionLabel} ${row.original.title}`}
      onClick={() => onActivateRow(row.original.id)}>{row.original.actionLabel}</Button> },
  ];
  const sorting = columns.filter(column => column.sortDirection).map(column => ({ id: column.id, desc: column.sortDirection === 'desc' }));
  return <DataTable columns={tableColumns} data={rows} getRowId={row => row.id}
    mode="manual" layout="viewport" density="compact" stickyHeader showPagination={false}
    rowSelection={selectedRowId ? { [selectedRowId]: true } : {}}
    sorting={sorting} onSortingChange={update => {
      const next = typeof update === 'function' ? update(sorting) : update;
      const item = next[0];
      onSortChange(item?.id ?? sorting[0]?.id ?? '', item ? (item.desc ? 'desc' : 'asc') : null);
    }}
    onRowActivate={row => { if (!row.actionDisabled) onActivateRow(row.id); }}
    rowActivationLabel={row => row.actionDescription ?? `${row.actionLabel} ${row.title}`}
  />;
}

// ─── Section detail panel ─────────────────────────────────────────────────────

function SectionDetailPanel({
  detail,
  onTabChange,
  onRowSelect,
}: {
  detail: StatusInventorySectionDetail;
  onTabChange: (tabId: string) => void;
  onRowSelect: (tabId: string, rowId: string) => void;
}) {
  const activeTab = detail.tabs.find(t => t.id === detail.activeTabId) ?? detail.tabs[0];
  return (
    <div>
      <div className="flex flex-col gap-3 bg-card/80 px-4 py-3 border-b border-border">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Device Detail</p>
          <h3 className="text-sm font-bold text-foreground mt-0.5">{detail.title}</h3>
          {detail.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{detail.description}</p>
          )}
        </div>
        <div className="flex gap-1 flex-wrap">
          {detail.tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "px-3 py-1.5 text-xs font-semibold rounded-md transition-colors",
                tab.id === activeTab?.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {activeTab && (
        <DetailTabContent
          tab={activeTab}
          onRowSelect={rowId => onRowSelect(activeTab.id, rowId)}
        />
      )}
    </div>
  );
}

function DetailTabContent({
  tab,
  onRowSelect,
}: {
  tab: StatusInventoryDetailTab;
  onRowSelect: (rowId: string) => void;
}) {
  if (!tab.rows.length) {
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-sm font-semibold text-foreground">{tab.emptyTitle}</p>
        <p className="mt-1 text-xs text-muted-foreground">{tab.emptyDescription}</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm border-collapse">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="w-8 px-4 py-2.5" />
            <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Name
            </th>
            {tab.columns.map(col => (
              <th
                key={col.id}
                className="hidden md:table-cell px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {tab.rows.map(row => {
            const primaryTone = row.badges?.[0]?.tone;
            const isSelected = row.id === tab.selectedRowId;
            return (
              <tr
                key={row.id}
                onClick={() => onRowSelect(row.id)}
                className={cn(
                  "cursor-pointer transition-colors",
                  isSelected
                    ? "bg-primary/10 border-l-2 border-l-primary"
                    : "hover:bg-muted/40 border-l-2 border-l-transparent"
                )}
              >
                <td className="px-4 py-2.5 w-8">
                  <div className="flex items-center justify-center">
                    <StatusDot tone={primaryTone} />
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-foreground leading-tight">{row.title}</div>
                  {row.subtitle && (
                    <div className="text-[11px] text-muted-foreground font-mono mt-0.5 line-clamp-1">
                      {row.subtitle}
                    </div>
                  )}
                  {row.badges?.length ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {row.badges.map(badge => (
                        <Badge key={badge.id} label={badge.label} tone={badge.tone} />
                      ))}
                    </div>
                  ) : null}
                </td>
                {tab.columns.map(col => (
                  <td key={col.id} className="hidden md:table-cell px-3 py-2.5 text-sm text-foreground/80">
                    {row.cells[col.id] || <span className="text-muted-foreground/50">—</span>}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Editor field ─────────────────────────────────────────────────────────────

function EditorField({
  field,
  onChange,
  onSelectorSearch,
  onSelectorRetry,
}: {
  field: StatusInventoryEditorField;
  onChange: (fieldId: string, value: string | boolean) => void;
  onSelectorSearch: StatusInventoryViewProps["onSelectorSearch"];
  onSelectorRetry: StatusInventoryViewProps["onSelectorRetry"];
}) {
  const descriptionId = field.description ? `${field.id}-description` : undefined;
  const errorId = field.error ? `${field.id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;

  if (field.kind === "checkbox") {
    return (
      <label
        className={cn(
          "flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-sm font-medium transition-colors",
          field.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-muted/50"
        )}
      >
        <Checkbox
          id={field.id}
          checked={Boolean(field.value)}
          disabled={field.disabled}
          aria-invalid={Boolean(field.error)}
          aria-describedby={describedBy}
          onCheckedChange={value => onChange(field.id, Boolean(value))}
        />
        <span className="text-foreground">{field.label}</span>
        {field.error && <span id={errorId} className="text-xs text-destructive">{field.error}</span>}
      </label>
    );
  }

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={field.id} className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {field.label}
        {field.required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {field.description && <p id={descriptionId} className="text-xs text-muted-foreground">{field.description}</p>}
      {field.kind === "select" || field.kind === "async-select" ? (
        <>
        {field.kind === "async-select" && (
          <input
            type="search"
            value={field.optionsSearchValue ?? ""}
            onChange={event => onSelectorSearch(field.id, event.target.value)}
            placeholder={`Search ${field.label.toLowerCase()}…`}
            aria-label={`Search ${field.label.toLowerCase()}`}
            disabled={field.disabled}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        )}
        <Select
          value={String(field.value ?? "")}
          onValueChange={value => onChange(field.id, value)}
          disabled={field.disabled || field.optionsLoading}
        >
          <SelectTrigger
            id={field.id}
            className="h-9 text-sm"
            aria-invalid={Boolean(field.error)}
            aria-describedby={describedBy}
          >
            <SelectValue placeholder={field.placeholder ?? "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map(option => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.optionsLoading && <p role="status" className="text-xs text-muted-foreground">Loading options…</p>}
        {field.optionsError && (
          <div className="flex items-center justify-between gap-2 text-xs text-destructive" role="alert">
            <span>{field.optionsError}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => onSelectorRetry(field.id)}>
              Retry
            </Button>
          </div>
        )}
        {!field.optionsLoading && !field.optionsError && !(field.options ?? []).length && (
          <p className="text-xs text-muted-foreground">{field.optionsEmptyLabel ?? "No options available."}</p>
        )}
        </>
      ) : field.kind === "textarea" ? (
        <textarea
          id={field.id}
          value={String(field.value ?? "")}
          placeholder={field.placeholder ?? undefined}
          disabled={field.disabled}
          aria-invalid={Boolean(field.error)}
          aria-describedby={describedBy}
          onChange={e => onChange(field.id, e.target.value)}
          rows={4}
          className="min-h-[80px] resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-xs text-foreground shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : (
        <input
          id={field.id}
          type={field.kind === "number" ? "number" : field.kind === "password" ? "password" : "text"}
          value={String(field.value ?? "")}
          placeholder={field.placeholder ?? undefined}
          disabled={field.disabled}
          aria-invalid={Boolean(field.error)}
          aria-describedby={describedBy}
          onChange={e => onChange(field.id, e.target.value)}
          className="h-8 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
      )}
      {field.error && (
        <p id={errorId} className="text-xs font-medium text-destructive">{field.error}</p>
      )}
    </div>
  );
}

// ─── Editor panel body (shared between desktop and mobile) ────────────────────

function EditorBody({
  fields,
  saving,
  deleting,
  saveLabel,
  deleteLabel,
  cancelLabel,
  onFieldChange,
  onSelectorSearch,
  onSelectorRetry,
  onSave,
  onDelete,
  onCancel,
}: {
  fields: StatusInventoryEditorField[];
  saving?: boolean;
  deleting?: boolean;
  saveLabel?: string | null;
  deleteLabel?: string | null;
  cancelLabel?: string | null;
  onFieldChange: (fieldId: string, value: string | boolean) => void;
  onSelectorSearch: StatusInventoryViewProps["onSelectorSearch"];
  onSelectorRetry: StatusInventoryViewProps["onSelectorRetry"];
  onSave: () => void;
  onDelete?: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {['Identity', 'Location', 'Monitoring', 'Credentials', 'Advanced'].map(group => {
          const groupFor = (field: StatusInventoryEditorField) =>
            field.kind === 'textarea' || ['external_ref', 'snmp_walk_params'].includes(field.id) ? 'Advanced'
            : /community|password|username|security_level|auth_protocol|priv_protocol|snmp_auth/.test(field.id) ? 'Credentials'
            : /site_id|device_id|interface_id|^lat$|^lng$/.test(field.id) ? 'Location'
            : /monitoring|discovery|snmp_version|snmp_port|^active$/.test(field.id) ? 'Monitoring' : 'Identity';
          const grouped = fields.filter(field => groupFor(field) === group);
          if (!grouped.length) return null;
          const content = grouped.map(field => <EditorField key={field.id} field={field} onChange={onFieldChange} onSelectorSearch={onSelectorSearch} onSelectorRetry={onSelectorRetry} />);
          return group === 'Advanced'
            ? <details key={group} open={grouped.some(field => Boolean(field.error)) || undefined} className="space-y-4 border-t border-border pt-4"><summary className="cursor-pointer text-sm font-medium">Advanced</summary>{content}</details>
            : <fieldset key={group} className="space-y-4 border-t border-border pt-4"><legend className="pr-2 text-xs font-semibold text-muted-foreground">{group}</legend>{content}</fieldset>;
        })}
      </div>
      <div className="flex flex-wrap gap-2 border-t border-border px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex-shrink-0">
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={saving || deleting}
          className="flex-1 sm:flex-none"
        >
          {saving ? "Saving…" : (saveLabel ?? "Save")}
        </Button>
        {onDelete && (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={onDelete}
            disabled={saving || deleting}
          >
            {deleting ? "Deleting…" : (deleteLabel ?? "Delete")}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCancel}
          disabled={saving}
        >
          {cancelLabel ?? "Cancel"}
        </Button>
      </div>
    </>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function StatusInventoryView({
  title,
  description,
  eyebrow,
  activeSectionId,
  sections,
  access,
  list,
  notices = [],
  selectedRowId,
  editor,
  confirmation,
  detail,
  onAccessAction,
  onSectionChange,
  onSearchChange,
  onClearQuery,
  onRemoveFilter,
  onClearFilters,
  onLifecycleChange,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onActivateRow,
  onCreate,
  onRefresh,
  onFieldChange,
  onSelectorSearch,
  onSelectorRetry,
  onSave,
  onRequestDelete,
  onRequestCloseEditor,
  onConfirm,
  onCancelConfirmation,
  onEditSelected,
  onDetailPageChange,
  onDetailSearchChange,
  quickViews,
  onQuickView,
  onCollectionHealth,
  onDetailTabChange,
  onDetailRowActivate,
  className,
}: StatusInventoryViewProps) {
  const activeSection = sections.find(s => s.id === activeSectionId) ?? sections[0];
  const hasQuery = Boolean(list.searchValue.trim() || list.activeFilters.length);
  const pageCount = Math.max(1, Math.ceil(list.total / Math.max(1, list.pageSize)));
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editor?.open || !editor.fields.some(field => field.error)) return;
    editorRef.current?.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
  }, [editor?.fields, editor?.open]);

  const editorBody = editor?.open ? (
    <EditorBody
      fields={editor.fields}
      saving={editor.saving}
      deleting={editor.deleting}
      saveLabel={editor.saveLabel}
      deleteLabel={editor.deleteLabel}
      cancelLabel={editor.cancelLabel}
      onFieldChange={onFieldChange}
      onSelectorSearch={onSelectorSearch}
      onSelectorRetry={onSelectorRetry}
      onSave={onSave}
      onDelete={onRequestDelete}
      onCancel={onRequestCloseEditor}
    />
  ) : null;

  return (
    <RegistryWorkspace className={cn("h-full min-w-0 bg-background", className)}>
      <header className="flex-shrink-0 border-b border-border bg-card px-4 py-3 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {eyebrow && (
              <p className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
                {eyebrow}
              </p>
            )}
            <h1 className="text-lg font-bold tracking-tight text-foreground">{title}</h1>
            {description && (
              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="min-h-11" onClick={onRefresh} disabled={list.initialLoading || list.refreshing}>
              <IconRefresh className={cn("h-3.5 w-3.5", list.refreshing && "animate-spin")} />
              <span>Refresh</span>
            </Button>
            {activeSection?.createLabel && !activeSection.readOnly && access.kind === "ready" && (
              <Button type="button" size="sm" className="min-h-11" onClick={() => onCreate(activeSection.id)}>
                <IconPlus className="h-3.5 w-3.5" />
                <span>{activeSection.createLabel}</span>
              </Button>
            )}
          </div>
        </div>
        {sections.length > 1 && (
          <div className="mt-3 max-w-xs">
            <Label htmlFor="status-inventory-section" className="sr-only">Inventory section</Label>
            <Select value={activeSectionId} onValueChange={value => onSectionChange(value as typeof activeSectionId)}>
              <SelectTrigger id="status-inventory-section" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sections.map(section => <SelectItem key={section.id} value={section.id}>{section.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </header>

      {notices.length > 0 && (
        <div className="flex-shrink-0 space-y-2 px-4 py-3 lg:px-6">
          {onCollectionHealth && <Button variant="link" size="sm" onClick={onCollectionHealth}>View collection health</Button>}
          {notices.map(notice => (
            <NoticeCard key={notice.id} notice={notice} />
          ))}
        </div>
      )}

      {access.kind !== "ready" ? (
        <div className="flex flex-1 items-center justify-center p-6" role="status">
          <div className="max-w-md text-center">
            <p className="text-sm font-semibold text-foreground">{access.title ?? "Status inventory unavailable"}</p>
            {access.description && <p className="mt-1 text-xs text-muted-foreground">{access.description}</p>}
            {access.actionLabel && onAccessAction && <Button className="mt-4" onClick={onAccessAction}>{access.actionLabel}</Button>}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className={cn("flex min-w-0 flex-1 flex-col overflow-hidden", detail && "hidden lg:flex")}>
            {quickViews && <div aria-label="Inventory views" className="flex shrink-0 flex-wrap gap-1 border-b border-border px-4 py-2">{quickViews.map(view => <Button key={view.id} size="sm" variant={view.active ? 'secondary' : 'ghost'} aria-pressed={view.active} onClick={() => onQuickView?.(view.id)}>{view.label}</Button>)}</div>}
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-border bg-card/20 px-4 py-2.5">
                <div className="relative min-w-0 flex-1 sm:max-w-xs">
                  <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    aria-label="Search inventory"
                    value={list.searchValue}
                    onChange={event => onSearchChange(event.target.value)}
                    placeholder={list.searchPlaceholder ?? `Search ${activeSection?.label.toLowerCase() ?? "records"}…`}
                    className="h-11 w-full rounded-md border border-input bg-background pl-8 pr-9 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                  {list.searchValue && (
                    <button type="button" onClick={onClearQuery} aria-label="Clear search" className="absolute right-1 top-1/2 min-h-11 min-w-11 -translate-y-1/2 text-muted-foreground">
                      <IconX className="mx-auto h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {activeSectionId === "links" && list.lifecycle && onLifecycleChange && (
                  <div className="w-36">
                    <Label htmlFor="status-link-lifecycle" className="sr-only">Link lifecycle</Label>
                    <Select value={list.lifecycle} onValueChange={value => onLifecycleChange(value as "active" | "suppressed")}>
                      <SelectTrigger id="status-link-lifecycle" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active links</SelectItem>
                        <SelectItem value="suppressed">Suppressed links</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {list.activeFilters.map(filter => (
                  <button key={filter.id} type="button" onClick={() => onRemoveFilter(filter.id)} className="min-h-11 rounded-md border border-border px-3 text-xs text-foreground">
                    {filter.label}: {filter.value} <span aria-hidden="true">×</span>
                  </button>
                ))}
                {list.activeFilters.length > 1 && <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={onClearFilters}>Clear filters</Button>}
                <span className="ml-auto flex-shrink-0 text-xs tabular-nums text-muted-foreground">
                  {list.total} {list.total === 1 ? "record" : "records"}
                </span>
            </div>
            <div className="sr-only" role="status" aria-live="polite">
              {list.refreshing ? "Refreshing inventory…" : editor?.saving ? "Saving changes…" : editor?.deleting ? "Deleting record…" : ""}
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {list.initialLoading ? <LoadingSkeleton /> :
                list.error ? (
                  <div className="px-6 py-12 text-center" role="alert">
                    <p className="text-sm font-semibold text-destructive">Unable to load inventory</p>
                    <p className="mt-1 text-xs text-muted-foreground">{list.error}</p>
                    <Button className="mt-4" variant="outline" onClick={onRefresh}>Retry</Button>
                  </div>
                ) : !list.rows.length ? (
                  <EmptyState
                    title={hasQuery ? (list.noResultsTitle ?? "No results found") : list.emptyTitle}
                    description={hasQuery ? (list.noResultsDescription ?? "No records match the current query.") : list.emptyDescription}
                  />
                ) : (
                  <InventoryTable
                    sectionLabel={activeSection?.label ?? "Inventory"}
                    columns={list.columns}
                    rows={list.rows}
                    selectedRowId={selectedRowId}
                    onActivateRow={onActivateRow}
                    onSortChange={onSortChange}
                  />
                )}

            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-3 py-2">
              <span className="text-xs text-muted-foreground">Page {Math.min(list.pageIndex + 1, pageCount)} of {pageCount}</span>
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="status-inventory-page-size" className="text-xs">Rows</Label>
                <select
                  id="status-inventory-page-size"
                  value={list.pageSize}
                  onChange={event => onPageSizeChange(Number(event.target.value))}
                  className="h-11 rounded-md border border-input bg-background px-2 text-sm"
                >
                  {list.pageSizeOptions.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
                <Button type="button" variant="outline" className="min-h-11" disabled={list.pageIndex <= 0 || list.initialLoading} onClick={() => onPageChange(list.pageIndex - 1)}>Previous</Button>
                <Button type="button" variant="outline" className="min-h-11" disabled={list.pageIndex + 1 >= pageCount || list.initialLoading} onClick={() => onPageChange(list.pageIndex + 1)}>Next</Button>
              </div>
            </div>
          </div>
          {detail && <aside aria-label="Device inspector" className="flex w-full min-w-0 flex-col overflow-hidden border-l border-border bg-card lg:w-[40%]">
            <div className="flex shrink-0 items-center justify-between border-b border-border p-3"><span className="text-sm font-medium">Device inspector</span><div className="flex gap-2"><Button variant="outline" onClick={onEditSelected}>Edit</Button><Button variant="ghost" onClick={onRequestCloseEditor}>Close</Button></div></div>
            <div className="flex shrink-0 gap-2 p-3"><input aria-label="Search device records" type="search" placeholder="Search device records…" value={detail.searchValue ?? ''} onChange={event => onDetailSearchChange?.(event.target.value)} className="h-10 min-w-0 flex-1 rounded border border-input bg-background px-3 text-sm" /></div>
            <div className="min-h-0 flex-1 overflow-auto"><SectionDetailPanel detail={detail} onTabChange={tabId => onDetailTabChange?.(tabId)} onRowSelect={(tabId, rowId) => onDetailRowActivate?.(tabId, rowId)} /></div>
            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border p-3"><span className="text-xs text-muted-foreground">{detail.total ?? 0} records</span><div className="flex gap-2"><Button variant="outline" disabled={!detail.pageIndex} onClick={() => onDetailPageChange?.((detail.pageIndex ?? 0) - 1)}>Previous</Button><Button variant="outline" disabled={((detail.pageIndex ?? 0) + 1) * 50 >= (detail.total ?? 0)} onClick={() => onDetailPageChange?.((detail.pageIndex ?? 0) + 1)}>Next</Button></div></div>
          </aside>}
          {editor?.open && (
            <aside ref={editorRef} className="hidden w-80 flex-shrink-0 flex-col overflow-hidden border-l border-border bg-card lg:flex xl:w-96">
              <div className="flex-shrink-0 border-b border-border px-5 py-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Editor</p>
                <h2 className="mt-0.5 text-sm font-bold text-foreground">{editor.title}</h2>
                {editor.description && <p className="mt-1 text-xs text-muted-foreground">{editor.description}</p>}
                {editor.mutationError && <p className="mt-2 text-xs text-destructive" role="alert">{editor.mutationError}</p>}
              </div>
              {editorBody}
            </aside>
          )}
        </div>
      )}

      {editor?.open && (
        <div className="fixed inset-0 z-40 flex bg-foreground/30 lg:hidden" role="dialog" aria-modal="true" aria-labelledby="status-inventory-editor-title">
          <div ref={editorRef} className="flex h-full w-full min-w-0 flex-col bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 id="status-inventory-editor-title" className="text-sm font-bold">{editor.title}</h2>
              <Button type="button" variant="ghost" size="icon" aria-label="Close editor" onClick={onRequestCloseEditor}><IconX className="h-5 w-5" /></Button>
            </div>
            {editor.mutationError && <p className="px-4 py-2 text-xs text-destructive" role="alert">{editor.mutationError}</p>}
            {editorBody}
          </div>
        </div>
      )}

      {confirmation?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 p-4" role="alertdialog" aria-modal="true" aria-labelledby="status-confirmation-title" aria-describedby="status-confirmation-description">
          <div className="w-full max-w-md rounded-lg border border-border bg-popover p-5 text-popover-foreground shadow-xl">
            <h2 id="status-confirmation-title" className="text-base font-bold">{confirmation.title}</h2>
            <p id="status-confirmation-description" className="mt-2 text-sm text-muted-foreground">{confirmation.description}</p>
            {confirmation.error && <p className="mt-3 text-sm text-destructive" role="alert">{confirmation.error}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={confirmation.busy} onClick={onCancelConfirmation}>{confirmation.cancelLabel ?? "Cancel"}</Button>
              <Button type="button" variant={confirmation.destructive ? "destructive" : "default"} disabled={confirmation.busy} onClick={onConfirm}>
                {confirmation.busy ? "Working…" : confirmation.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </RegistryWorkspace>
  );
}
